// ============================================================================
// NORMALISATION des données d'identité (SPEC §28.1). UTILITÉ : une donnée saisie au comptoir
// est sale (espaces, accents, formats libres) — on la RANGE avant stockage ET avant comparaison.
// ⚠️ C'est la brique dont dépend `matchDuplicates` : deux fiches ne se reconnaissent doublons que
// si leurs clés sont normalisées À L'IDENTIQUE (un « 06 12 34 56 78 » et un « +33612345678 » sont
// le MÊME téléphone). Pur, zéro-dép, golden-testable. Réf. SPEC §28.1, §24.6quinquies C.
// ============================================================================

/**
 * Indicatifs téléphoniques par pays (ISO 3166 alpha-2). Volontairement RESTREINT à l'Europe de
 * proximité : le pays par défaut vient de la config magasin (§28.1) et les autres numéros
 * arrivent avec leur indicatif (`+…`/`00…`), donc cette table ne sert QU'au format national.
 */
const CALLING_CODES: Record<string, string> = {
  FR: '33', CH: '41', BE: '32', LU: '352', DE: '49', ES: '34',
  IT: '39', GB: '44', MC: '377', AD: '376', NL: '31', PT: '351',
};

/**
 * Nettoie une partie de nom (§28.1) : garde lettres (TOUS accents), espace, tiret, apostrophe ;
 * retire chiffres et caractères spéciaux ; `trim` + écrase les espaces multiples.
 * (`O'Neil`, `d'Artagnan`, `Jean-Pierre` restent intacts ; `Jean2@` → `Jean`.)
 *
 * ⚠️ Apostrophes et tirets TYPOGRAPHIQUES ramenés à leur forme ASCII **avant** filtrage : un clavier
 * tactile (correction auto iOS/Android) et un copier-coller depuis Word produisent `’` et `–` là où
 * l'opérateur croit taper `'` et `-`. Sans ça (a) le caractère serait purement SUPPRIMÉ (`O’Neil` →
 * `ONeil` — bug attrapé par les golden tests), (b) la MÊME personne saisie deux fois donnerait deux
 * chaînes différentes → les homonymes (indice mou §C) ne se verraient plus. Une seule forme canonique.
 */
export function cleanNamePart(raw: string): string {
  return (raw ?? '')
    .replace(/[‘’ʼ‛]/g, "'") // ‘ ’ ʼ ‛ → '
    .replace(/[‐‑‒–—]/g, '-') // ‐ ‑ ‒ – — → -
    .replace(/[^\p{L}\s'-]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** NOM de famille prêt à stocker : nettoyé + **MAJUSCULES** (§28.1). */
export function normalizeLastName(raw: string): string {
  return cleanNamePart(raw).toLocaleUpperCase('fr-FR');
}

/** Prénom prêt à stocker : nettoyé (casse laissée telle quelle — §28.1 n'impose que le NOM). */
export function normalizeFirstName(raw: string): string {
  return cleanNamePart(raw);
}

/** « NOM Prénom » — forme concaténée remontée au back-office (§28.1). */
export function fullName(lastName: string, firstName?: string | null): string {
  const f = cleanNamePart(firstName ?? '');
  return f ? `${normalizeLastName(lastName)} ${f}` : normalizeLastName(lastName);
}

/** Email prêt à stocker ET à comparer : `trim` + **minuscules** (tranché 2026-07, §28.1). null si vide. */
export function normalizeEmail(raw?: string | null): string | null {
  const v = (raw ?? '').trim().toLowerCase();
  return v ? v : null;
}

/**
 * Téléphone → **E.164** (`+33612345678`). Règle tranchée 2026-07 (§28.1) : le pays par défaut est un
 * **RACCOURCI de saisie, jamais une contrainte**.
 *  - **`+…` ou `00…` → l'indicatif SAISI fait foi** (le défaut est ignoré) : cas du magasin frontalier
 *    (Annemasse → `+41…` genevois) — nominal, aucun réglage à changer.
 *  - **Format national (`06…`)** → on retire le 0 initial et on préfixe l'indicatif du **pays par défaut**
 *    (config magasin, repli `FR`).
 * Retourne `null` si structurellement inexploitable (trop court/long — E.164 plafonne à 15 chiffres).
 * ⚠️ Normalisation STRUCTURELLE seulement : la validation stricte « ce numéro existe-t-il dans ce pays ? »
 * vit à la SAISIE (produit), pas dans le cœur — ici on garantit une clé de comparaison stable.
 */
export function normalizePhoneE164(raw?: string | null, defaultCountry = 'FR'): string | null {
  const s = (raw ?? '').trim();
  if (!s) return null;

  const isPlus = s.startsWith('+');
  const digitsOnly = s.replace(/\D/g, '');
  if (!digitsOnly) return null;

  let e164Digits: string;
  if (isPlus) {
    e164Digits = digitsOnly; // « +41 79… » → l'indicatif saisi fait foi
  } else if (digitsOnly.startsWith('00')) {
    e164Digits = digitsOnly.slice(2); // « 0041 79… » = préfixe international européen
  } else {
    const cc = CALLING_CODES[defaultCountry.toUpperCase()];
    if (!cc) return null; // pays par défaut inconnu → on préfère null qu'un numéro faux
    e164Digits = cc + digitsOnly.replace(/^0+/, ''); // national : le 0 de service saute
  }

  // E.164 : 15 chiffres max ; en deçà de 8, ce n'est pas un numéro exploitable.
  if (e164Digits.length < 8 || e164Digits.length > 15) return null;
  return `+${e164Digits}`;
}

/**
 * LES CIVILITÉS QUE LES FICHES PROPOSENT — des CODES, la valeur qui part en base.
 *
 * ⚠️ UN CODE, JAMAIS UN LIBELLÉ : la valeur est celle-ci dans toutes les langues ; seul son LIBELLÉ se
 * traduit à l'écran (« Mme » se lit « Ms » ou « Sra. »). Le back-office enregistrait le libellé traduit
 * — « Ms » en base sous un compte anglais, une valeur que les caisses, les campagnes et les documents
 * ne reconnaissaient plus (une valeur qui part en base n'est pas un texte).
 * ⚠️ CE SONT LES FORMES ABRÉGÉES DU RÉFÉRENTIEL « Civilités » de la Console (`m` → « M. », `mme` →
 *    « Mme ») : la caisse et le back-office les offraient chacun en dur. Faire lire ce référentiel par les
 *    fiches — et lui donner des libellés par langue — est une décision ouverte ; d'ici là, cette liste
 *    est la SEULE que les deux fiches emploient.
 */
export const CIVILITY_CODES = ['M.', 'Mme'] as const;

/** Une civilité de la liste fermée. */
export type CivilityCode = (typeof CIVILITY_CODES)[number];

/** Cette valeur est-elle un code de civilité ? (une valeur reprise peut ne pas l'être : « Dr »). */
export function isCivilityCode(v: unknown): v is CivilityCode {
  return typeof v === 'string' && (CIVILITY_CODES as readonly string[]).includes(v);
}

/**
 * Les formes que chaque source écrit pour CHAQUE code — français, anglais, espagnol, abrégées ou non.
 * ⚠️ DES ÉQUIVALENCES CERTAINES SEULEMENT : « Ms » et « Mrs » désignent une femme sans préjuger de son
 *    état civil, comme « Mme ». « Mademoiselle », « Miss » et « Srta. » N'Y SONT PAS : aucun code ne les
 *    porte, donc les ramener à « Mme » perdrait ce que la personne a déclaré.
 * ⚠️ LES FORMES ÉTRANGÈRES Y SONT POUR LE DÉFAUT DÉJÀ PAYÉ : un « Ms » écrit par le back-office sous un
 *    compte anglais, ou importé d'un fichier, rejoint son code à la prochaine écriture.
 */
const CIVILITY_FORMS: Readonly<Record<CivilityCode, readonly string[]>> = {
  'M.': ['m', 'mr', 'monsieur', 'mister', 'sr', 'señor', 'senor'],
  Mme: ['mme', 'madame', 'mrs', 'ms', 'sra', 'señora', 'senora'],
};

/**
 * CIVILITÉ → son CODE (« M. », « Mme », « Mlle »), par ÉQUIVALENCE SANS PERTE.
 *
 * UTILITÉ : chaque source écrit la civilité dans sa langue — le legacy en toutes lettres (mesuré à la
 * reprise : « Madame », « Monsieur », « Mademoiselle »), un import Excel en abrégés libres (« MR »,
 * « MME »), un fichier étranger en anglais ou en espagnol. Sans forme canonique, la MÊME civilité se
 * range dans plusieurs cases (segments d'écran, publipostage, stats) et le vocabulaire de la colonne
 * dérive.
 *
 * ⚠️ SEULES LES ÉQUIVALENCES CERTAINES SE NORMALISENT (`CIVILITY_FORMS`). Toute autre valeur
 * (« Mademoiselle », « Dr »…) est CONSERVÉE TELLE QUELLE — la traduire perdrait ce que la personne a
 * déclaré. Vide → null : forcer
 * « M. » sur une civilité inconnue inventerait une donnée. C'est la règle des PORTES D'ÉCRITURE
 * (reprise, BO, caisse, imports) — jamais un filtre de lecture (Forme I : l'écran affiche ce que la base
 * tient).
 */
export function normalizeCivility(raw?: string | null): string | null {
  const brut = (raw ?? '').trim();
  if (!brut) return null;
  const x = brut.toLowerCase().replace(/\.+$/, '');
  for (const code of CIVILITY_CODES) {
    if (CIVILITY_FORMS[code].includes(x)) return code;
  }
  return brut;
}

/** Nombre réel de jours d'un mois (gère les bissextiles) — base de la validation stricte §28.1. */
function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/**
 * Date de naissance saisie LIBREMENT au comptoir → **ISO `AAAA-MM-JJ`** (§28.1, saisie sans calendrier).
 * Séparateurs acceptés : espace, `-`, `/`, `\`, `.` **ou aucun** (`01011990`). Ordre **JJ MM AAAA**.
 * Années sur **2 chiffres** : `≤ 30` → 20xx, sinon 19xx. **Validation stricte** (mois 1-12, jour ≤ nb
 * RÉEL de jours du mois → `31/02` refusé). Retourne `null` si illisible/invalide — la valeur persistée
 * est TOUJOURS l'ISO, jamais la chaîne libre.
 */
export function parseBirthdate(raw?: string | null): string | null {
  const s = (raw ?? '').trim();
  if (!s) return null;

  const parts = s.split(/[\s\-/\\.]+/).filter(Boolean);
  let d: number, m: number, y: number;

  if (parts.length === 3) {
    const [dd, mm, yy] = parts;
    if (!dd || !mm || !yy) return null;
    // ⚠️ 🔴 L'ISO EST ACCEPTÉ EN ENTRÉE, PARCE QUE C'EST LA FORME QUE CETTE FONCTION ÉMET. Le refuser
    //    faisait perdre toute date déjà normalisée : la caisse range l'ISO dans son brouillon, le
    //    renvoyait ici à l'enregistrement, et « 1985-03-12 » se lisait « jour 1985 » — donc `null`,
    //    donc une date de naissance EFFACÉE sans un mot. Le back-office, lui, retournait la chaîne
    //    avant d'appeler : un contournement chez UN appelant est le signe que le cœur manque un cas.
    // ⚠️ AUCUNE AMBIGUÏTÉ À TRANCHER : un jour ne s'écrit jamais sur quatre chiffres, donc une
    //    première partie de quatre chiffres est une ANNÉE, et l'ordre est alors AAAA-MM-JJ.
    if (dd.length === 4) {
      y = Number(dd);
      m = Number(mm);
      d = Number(yy);
    } else {
    if (yy.length !== 2 && yy.length !== 4) return null;
    d = Number(dd);
    m = Number(mm);
    y = Number(yy);
    if (yy.length === 2) y = y <= 30 ? 2000 + y : 1900 + y; // ≤30 → 20xx, sinon 19xx (§28.1)
    }
  } else if (parts.length === 1) {
    const digits = parts[0];
    if (!digits || !/^\d+$/.test(digits)) return null;
    if (digits.length === 8) {
      // JJMMAAAA (saisie sans aucun séparateur)
      d = Number(digits.slice(0, 2));
      m = Number(digits.slice(2, 4));
      y = Number(digits.slice(4, 8));
    } else if (digits.length === 6) {
      // JJMMAA
      d = Number(digits.slice(0, 2));
      m = Number(digits.slice(2, 4));
      const yy = Number(digits.slice(4, 6));
      y = yy <= 30 ? 2000 + yy : 1900 + yy;
    } else {
      return null;
    }
  } else {
    return null;
  }

  if (!Number.isInteger(d) || !Number.isInteger(m) || !Number.isInteger(y)) return null;
  if (m < 1 || m > 12) return null;
  if (y < 1900 || y > 2100) return null;
  if (d < 1 || d > daysInMonth(y, m)) return null; // 31/02 refusé (nb RÉEL de jours)

  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${y}-${p2(m)}-${p2(d)}`;
}
